/**
 * Design fixtures for the voice/video call surface.
 *
 * This clone ships without WebRTC (see `services/webRTCManager.ts`). Nothing
 * here opens a peer connection, touches `getUserMedia`, or asks for a
 * permission — the call screen is populated from static data plus canvas-
 * generated video tracks so the whole layout can be designed offline.
 *
 * Real participants arrive over Supabase Realtime *presence*, which is
 * ephemeral: with no second browser connected, presence is always just you,
 * and the call screen renders a single lonely tile. These fixtures are merged
 * into that presence payload (see `hooks/useRealtimeSync.ts`) so every tile
 * state — muted, camera-on, screen-sharing, speaking — is visible.
 *
 * Toggle with `VITE_DESIGN_FIXTURES=false` to see the real (empty) state.
 */

export interface FixtureParticipant {
  id: string;
  name: string;
  avatar: string;
  isMuted: boolean;
  isCameraOn: boolean;
  isScreenSharing: boolean;
  isWatchTogetherHost: boolean;
  currentGame: string | null;
  currentGameSessionId: string | null;
  /** Tile placeholder colour, as an HSL hue (0-360). */
  hue: number;
}

/** Enabled unless explicitly switched off. */
export const FIXTURES_ENABLED =
  import.meta.env.VITE_DESIGN_FIXTURES !== 'false';

/**
 * Deliberately covers every tile permutation the designer has to style:
 * camera on, camera off (avatar fallback), muted, screen-sharing, and
 * in-game (the 🎮 pill). Ids are namespaced so they can never collide with
 * a real seeded profile uuid.
 */
export const FIXTURE_PARTICIPANTS: ReadonlyArray<FixtureParticipant> = [
  {
    id: 'fixture-ripley',
    name: 'Ripley',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=ripley',
    isMuted: false,
    isCameraOn: true,
    isScreenSharing: false,
    isWatchTogetherHost: false,
    currentGame: null,
    currentGameSessionId: null,
    hue: 285,
  },
  {
    id: 'fixture-hicks',
    name: 'Hicks',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=hicks',
    isMuted: true,
    isCameraOn: false,
    isScreenSharing: false,
    isWatchTogetherHost: false,
    currentGame: null,
    currentGameSessionId: null,
    hue: 190,
  },
  {
    id: 'fixture-vasquez',
    name: 'Vasquez',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=vasquez',
    isMuted: false,
    isCameraOn: true,
    isScreenSharing: true,
    isWatchTogetherHost: false,
    currentGame: null,
    currentGameSessionId: null,
    hue: 45,
  },
  {
    id: 'fixture-bishop',
    name: 'Bishop',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=bishop',
    isMuted: false,
    isCameraOn: false,
    isScreenSharing: false,
    isWatchTogetherHost: false,
    currentGame: 'Cyber Chess',
    currentGameSessionId: 'fixture-session-1',
    hue: 130,
  },
];

/** Shape `useRealtimeSync` pushes into `voiceParticipants[channelId]`. */
export function fixtureParticipantsForChannel() {
  return FIXTURE_PARTICIPANTS.map((p) => ({
    id: p.id,
    name: p.name,
    avatar: p.avatar,
    isMuted: p.isMuted,
    isCameraOn: p.isCameraOn,
    isScreenSharing: p.isScreenSharing,
    isWatchTogetherHost: p.isWatchTogetherHost,
    currentGame: p.currentGame,
    currentGameSessionId: p.currentGameSessionId,
  }));
}

/** Shape `peerVoiceStates` expects, keyed by participant id. */
export function fixturePeerVoiceStates() {
  const out: Record<string, any> = {};
  for (const p of FIXTURE_PARTICIPANTS) {
    out[p.id] = {
      isMuted: p.isMuted,
      isCameraOn: p.isCameraOn,
      isScreenSharing: p.isScreenSharing,
      currentGame: p.currentGame,
      currentGameSessionId: p.currentGameSessionId,
    };
  }
  return out;
}

/**
 * Stand-in for `navigator.mediaDevices.getUserMedia({ video: true })`.
 *
 * The camera toggle used to acquire a real webcam track and hand it to the
 * peer mesh. There is no mesh now, so prompting for a camera would cost the
 * designer a permission dialog and return a track nothing consumes. With
 * fixtures on we return a canvas placeholder instead; with fixtures off the
 * real API is used, so the original behaviour is one env var away.
 */
export async function acquireCameraStream(): Promise<MediaStream | null> {
  if (FIXTURES_ENABLED) return createPlaceholderStream('You', 320);
  return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
}

/**
 * Stand-in for `navigator.mediaDevices.getDisplayMedia()`. Same reasoning as
 * `acquireCameraStream` — with no peers to send to, the OS screen-picker is
 * pure friction, so the screen-share tile gets a labelled placeholder.
 */
export async function acquireScreenStream(
  constraints: MediaStreamConstraints,
): Promise<MediaStream | null> {
  if (FIXTURES_ENABLED) return createPlaceholderStream('Your screen', 210);
  return navigator.mediaDevices.getDisplayMedia(constraints);
}

/**
 * A real `MediaStream` backed by an animated canvas — no camera, no
 * permission prompt, but `VideoTile` renders it through the same
 * `srcObject` path it uses for a live peer, so nothing downstream needs a
 * special case.
 *
 * Returns null where `captureStream` is unavailable (older Safari, some
 * WebViews); callers fall back to the avatar tile, which is the same
 * fallback used for a camera-off peer.
 */
export function createPlaceholderStream(
  label: string,
  hue: number,
): MediaStream | null {
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');
  if (!ctx || typeof (canvas as any).captureStream !== 'function') return null;

  const start = performance.now();
  let raf = 0;

  const draw = () => {
    const t = (performance.now() - start) / 1000;

    const bg = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    bg.addColorStop(0, `hsl(${hue}, 55%, 22%)`);
    bg.addColorStop(1, `hsl(${(hue + 40) % 360}, 60%, 10%)`);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Slow drifting rings, so the tile is visibly a live video track rather
    // than a still image — makes motion and border treatments easy to judge.
    ctx.strokeStyle = `hsla(${hue}, 90%, 70%, 0.28)`;
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const r = 60 + i * 55 + Math.sin(t * 0.8 + i) * 14;
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = '600 40px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, canvas.width / 2, canvas.height / 2);

    ctx.font = '500 18px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText('design fixture · no live video', canvas.width / 2, canvas.height / 2 + 44);

    raf = requestAnimationFrame(draw);
  };
  draw();

  const stream = (canvas as any).captureStream(24) as MediaStream;

  // Stop the rAF loop when the caller stops the track, so ended fixtures
  // don't keep painting offscreen canvases for the life of the tab.
  const [track] = stream.getVideoTracks();
  if (track) {
    const originalStop = track.stop.bind(track);
    track.stop = () => {
      cancelAnimationFrame(raf);
      originalStop();
    };
  }

  return stream;
}
